import Modal from '@mui/material/Modal';
import { useSelector, useDispatch } from "react-redux";
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import Report from "./Company/ReportOld.js";
import Loader from "../../components/Loader.js";
import DiligencePack from "./Company/DiligencePack";
import SidebarIndex from "./Company/SidebarIndexOld.js";
import { updateUser } from "../../reducers/userReducer";
import AnimatedPage from "../../components/AnimatedPage";
import { setCompany } from "../../reducers/companyReducer";
import { toggleModal } from "../../reducers/modalReducer";
import ResearchAidReport from "./Company/ResearchAid/ResearchAidReport.js";
import SiderbarResearchAid from "./Company/ResearchAid/SiderbarResearchAid.js";
import PdfViewer from "../../components/PdfViewer";
import { getRequest, postRequest, showMessage, setToken } from "../../config";

export default function DiligencePackExternal() {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const sectionRefs = useRef([]);
    const { id: companyId } = useParams();

    const user = useSelector(state => state.user);
    const modal = useSelector(state => state.modal);
    const company = useSelector(state => state.company?.selectedCompany || {});

    let [report, setReport] = useState({});
    let [reportType, setReportType] = useState('');
    let [isShowReport, setIsShowReport] = useState(false);
    let [isDirectorsReport, setIsDirectorsReport] = useState(false);
    let [isLoader, setIsLoader] = useState({ show: false, message: 'Loading...' });
    let [aiEnhanced, setAiEnchaned] = useState(false);
    let [directors, setDirectors] = useState(company?.['directors'] || []);


    useEffect(() => {
        (async () => {
            // Handle new shareCode format
            if (location.pathname.includes('shareCode')) {
                const pathParts = location.pathname.split('/');
                const codeIndex = pathParts.indexOf('shareCode');
                const companyIndex = pathParts.indexOf('diligence-pack');

                if (codeIndex !== -1 && companyIndex !== -1) {
                    const code = pathParts[codeIndex + 1];
                    const companyId = pathParts[companyIndex + 1];
                    // Call API to validate code and get permissions
                    const response = await getRequest(`/company/external-permission/${companyId}/${code}/company`, 'return-response');
                    if (response?.code === 200) {
                        // Set user and company in Redux
                        dispatch(updateUser(response.body.user));
                        // Add permissions to company object before storing in Redux
                        const companyWithPermissions = { ...response.body.company, externalUserPermissions: response.body.permissions };
                        dispatch(setCompany(companyWithPermissions));
                        return;
                    } else {
                        showMessage(response?.message || 'Invalid or expired share link');
                        setTimeout(() => navigate('/'), 3000);
                    }
                }
            }
        })();
    }, [location.pathname, navigate, companyId]);

    useEffect(() => {
        if (company?.['directors']) setDirectors(company['directors']);
    }, [company]);


    useEffect(() => {
        if (report?.['type'] && !isShowReport && !isDirectorsReport) {
            setReportType('report');
            getModule(report?.['type']);
        }
        if (report?.['htmlReport'] && report?.['reportType'] && !isShowReport) {
            setReportType('researchaid');
            navigate(`#${report['_id']}?${report['reportType']}-researchaid`);
            setIsShowReport(true);

            // Fetch Papermill PDF for ResearchAid reports
            if (isResearchAidReportType(report['reportType']) && !report.reportUrl) {
                getResearchAidReport(report);
            }
        }
    }, [report]);

    useEffect(() => {
        if (reportType === 'report' && report?.['type'] && !isShowReport) {
            const moduleName = report['type'];
            const moduleHasSections = company?.modules?.[moduleName]?.sections?.length > 0;
            if (moduleHasSections) setIsShowReport(true);
        }
    }, [company.modules, report, reportType, isShowReport]);

    useEffect(() => {
        if (isDirectorsReport) {
            dispatch(toggleModal({ open: 'showDirectors' }));
            setIsDirectorsReport(false);
        }
    }, [isDirectorsReport]);

    // Papermill modules - use getModuleReport API
    const PAPERMILL_MODULES = ['Investment Readiness', 'repository', 'warranty-disclosure', 'directors-questionnaire'];
    const isPapermillModule = (moduleType) => {
        return PAPERMILL_MODULES.includes(moduleType) || moduleType?.startsWith('business-audit-') || moduleType?.startsWith('directors-questionnaire');
    };

    // ResearchAid report types - use getModuleReport API with reportType
    const RESEARCHAID_REPORT_TYPES = ['marketReport', 'competitorReport', 'routeToMarketReport', 'TCAR3', 'swotAnalysis', 'insuranceGapAnalysis'];
    const isResearchAidReportType = (reportType) => RESEARCHAID_REPORT_TYPES.includes(reportType);

    // Fetch ResearchAid report PDF
    const getResearchAidReport = async (reportData) => {
        try {
            if (!reportData?._id || !reportData?.reportType) return;
            setIsLoader({ show: true, message: 'Loading Report PDF...' });
            const response = await postRequest(`/company/getModuleReport`, { companyId, reportType: reportData.reportType, reportId: reportData._id });
            setIsLoader({ show: false, message: 'Loading...' });
            if (response?.code === 200 && response?.body?.reportUrl) setReport(prev => ({ ...prev, reportUrl: response.body.reportUrl }));
        } catch (err) {
            console.error("Error fetching ResearchAid report PDF:", err);
            setIsLoader({ show: false, message: 'Loading...' });
        }
    };

    const getModule = async (moduleName) => {
        try {
            if (!moduleName) return;

            // Use getModuleReport for Papermill modules
            if (isPapermillModule(moduleName)) {
                setIsLoader({ show: true, message: 'Loading Report...' });
                let response = await postRequest(`/company/getModuleReport`, { companyId, moduleName });
                setIsLoader({ show: false, message: 'Loading...' });
                if (response?.code === 200 && response?.body) {
                    const finalUpdatedData = {
                        ...company,
                        modules: {
                            ...company['modules'],
                            [moduleName]: {
                                ...company['modules']?.[moduleName],
                                reportUrl: response.body.reportUrl,
                                reportGeneratedAt: response.body.reportGeneratedAt,
                                sections: response.body.sections || [],
                                stats: response.body.stats || {},
                                type: response.body.type || moduleName
                            }
                        }, from: "papermill"
                    };
                    await dispatch(setCompany(finalUpdatedData));
                }
                return;
            }

            // Original getModule logic for non-Papermill modules
            else if (!company['modules']?.[moduleName]?.['sections']) {
                setIsLoader({ show: true, message: 'Loading...' });
                let obj = { companyId, moduleName };
                let response = await postRequest(`/company/getModule`, obj);
                setIsLoader({ show: false, message: 'Loading...' });
                if (response['code'] != 200) return;
                if (response && response?.body) {
                    const finalUpdatedData = { ...company, modules: { ...company['modules'], ...response['body'] }, from: "getModule" }
                    await dispatch(setCompany(finalUpdatedData));
                }
            }
        } catch (err) {
            setIsShowReport(false);
            console.error("Error fetching module data:", err);
            showMessage("An error occurred while getting the module");
        } finally {
            setIsLoader({ show: false, message: 'Loading...' });
        }
    };

    const sortSections = (a, b) => {
        let valueA = (a?.sectionNo || a?.no).match(/\d+/g);
        let valueB = (b?.sectionNo || b?.no).match(/\d+/g);

        return valueA - valueB;
    };
    const sanitiseTab = t => decodeURIComponent(t).replace(/(-sync)?-report$/, "");

    const showPopupPercent = (percent, type) => {
        return <span className={`${percent > 67 ? (type === 'Risk' ? 'c9' : 'c34') : percent > 33 ? 'c39' : percent > 0 ? (type === 'Risk' ? 'c34' : 'c9') : 'c28'}`}>{percent}% {type}</span>;
    };

    const handleDirectorReportView = async (director) => {
        dispatch(toggleModal({ open: '' }));
        const moduleName = `directors-questionnaire_${director['_id']}`;
        setReport({ type: moduleName });
        setReportType('report');
        await getModule(moduleName);
    };

    return (
        <AnimatedPage>
            <div class='py4rem'>
                {isLoader['show'] && <Loader message={isLoader['message']} />}
                {!company?.['_id']
                    ? <Loader message='Loading Diligence Pack...' />
                    : <>
                        {isShowReport &&
                            <div class='w100 df jce'>
                                <div onClick={() => {
                                    setIsShowReport(false); setReport({}); setReportType('');
                                    if (window.location.hash) window.history.replaceState(null, '', location.pathname);
                                }} className='cp df aic jcc bg4 c1 fw400 fs1rem br30px px2rem py0-5rem mr7rem'>
                                    <i class="fa fa-arrow-circle-left c1 mr0-5rem" /> Back to Diligence
                                </div>
                            </div>
                        }
                        {company?.['_id'] && Object.keys(company).length > 0 &&
                            <>{(Object.keys(report).length > 0 && isShowReport)
                                ? <div className="td250 px4rem bg1 py2rem w100 df">
                                    {reportType == 'researchaid'
                                        ? <>
                                            {report.reportUrl
                                                ? <div className="w100 df jcc">
                                                    <PdfViewer src={report.reportUrl} className="w90 br10px" style={{ height: '100vh' }} title="Papermill Report PDF" />
                                                </div>
                                                : <>
                                                    <div className="w80 mr1rem dg bg5 py2rem">
                                                        <ResearchAidReport company={company} tab={`${report['reportType']}-researchaid`} />
                                                    </div>
                                                    <SiderbarResearchAid company={company} tab={`${report['reportType']}-researchaid`} />
                                                </>
                                            }
                                        </>
                                        : <>
                                            <div className="w80 mr3rem dg bg5 py2rem">
                                                <Report sectionRefs={sectionRefs} user={user} company={company} module={company.modules[report['type']]} tab={`${report['type']}-report`} sortSections={sortSections}
                                                    aiEnhanced={aiEnhanced} setAiEnchaned={setAiEnchaned}
                                                    moduleCertification={company?.['moduleCertification'][report?.['type'].includes('business-audit') ? 'business-audit-' + company['testType'] : report?.['type']]} />
                                            </div>
                                            <SidebarIndex sectionRefs={sectionRefs} user={user} company={company} module={company.modules[report['type']]} tab={`${report['type']}-report`} sortSections={sortSections}
                                                moduleCertification={company?.['moduleCertification'][report?.['type'].includes('business-audit') ? 'business-audit-' + company['testType'] : report?.['type']]} sanitiseTab={sanitiseTab} setIsLoader={setIsLoader}
                                                setCompany={() => { }} setTab={() => { }} isExternalPage={true} />
                                        </>
                                    }
                                </div>
                                : <div class='container'><DiligencePack company={company} setReport={setReport} setIsDirectorsReport={setIsDirectorsReport} isExternal={true} /></div>
                            }</>}
                    </>}
            </div>

            {/* Show Directors Modal */}
            <Modal open={modal['open'] == 'showDirectors'} onClose={() => dispatch(toggleModal({ open: '' }))} className="backdrop showDirectors td500 zi4 pf t0 l0 w100vw h100vh df jcc aic">
                <div className="pr maw800px inner-showDirectors pr">
                    <button className="close-popup" onClick={() => dispatch(toggleModal({ open: '' }))}>
                        <svg style={{ position: 'relative', top: '4px' }} className="pen fds h1rem f1 mr0-5rem" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                            <path className="pen fa-primary" d="M310.6 361.4c12.5 12.5 12.5 32.75 0 45.25C304.4 412.9 296.2 416 288 416s-16.38-3.125-22.62-9.375L160 301.3L54.63 406.6C48.38 412.9 40.19 416 32 416S15.63 412.9 9.375 406.6c-12.5-12.5-12.5-32.75 0-45.25l105.4-105.4L9.375 150.6c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L160 210.8l105.4-105.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-105.4 105.4L310.6 361.4z" />
                        </svg>
                    </button>
                    <div className='top-header'>
                        <h1 className="text-gradient fw600 tac mb1rem">LIST OF QUESTIONNAIRES</h1>
                    </div>
                    <div className='table-area-showDirectors'>
                        <table id="table" className="table-showDirectors">
                            <thead className="ps t0">
                                <tr>
                                    <th>Name</th>
                                    <th>Designation</th>
                                    <th>Progress</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {directors && directors.length > 0 && <>
                                    {directors.map((item, idx) => (
                                        <tr key={idx}>
                                            <td className={`${item['headShot'] && 'df aic '} c6 fw500 ttc`}>
                                                {item['headShot'] && <img src={item['headShot']['location']} className="w2rem h2rem df jcc aic br100 bg3 c1 mr0-5rem" />}
                                                {item['name']}
                                            </td>
                                            <td className='c6 fw500 ttc'>{item['designation']}</td>
                                            <td className='p1rem c6 fw500 ttc'>({showPopupPercent(company?.['modules']?.[`directors-questionnaire_${item['_id']}`]?.['stats']?.['completionPercent'] || 0, 'Completeness')} / {showPopupPercent(company?.['modules']?.[`directors-questionnaire_${item['_id']}`]?.['stats']?.['riskPercent'] || 0, 'Risk')})</td>
                                            <td><button onClick={() => handleDirectorReportView(item)} className="cp bg4 bg3-hover c1 tac fs0-75rem fw500 px1rem py0-5rem br5px">View</button></td>
                                        </tr>
                                    ))}
                                </>}
                            </tbody>
                        </table>
                        {directors && directors.length == 0 &&
                            <div className='tac py1rem'>
                                <h2 className="text-gradient fw600">No Directors by this Company</h2>
                            </div>
                        }
                        <div className='logo-modal'>
                            <img className="h2rem" src={require(`../../../images/logo.svg`)} alt="TODO" />
                        </div>
                    </div>
                </div>
            </Modal>
        </AnimatedPage>
    );
}